<template>
  <div
    class="group flex flex-col gap-2 p-4 pt-3"
    :class="[
      fullScreen
        ? 'fixed inset-0 z-100 bg-white'
        : 'relative h-72 rounded-md border',
      !fullScreen && localElement?.stat_type === 'sales_turnover'
        ? 'col-span-4'
        : !fullScreen && localElement?.format === 'extended'
          ? 'col-span-2'
          : 'col-span-1'
    ]"
  >
    <LoadingComponent v-if="isElementLoading" />
    <div class="absolute right-0 top-0 mr-2 mt-2 flex flex-row gap-1">
      <button @click="fullScreen = !fullScreen">
        <FontAwesomeIcon
          :icon="['fal', fullScreen ? 'arrows-minimize' : 'arrows-maximize']"
        />
      </button>
      <BaseDropDownMenu
        v-if="$can('manage_dashboards')"
        :is-button="false"
        size="xs"
        :options="actionMenuOptions"
        :is-loading="isLoading"
      />
    </div>

    <template v-if="!localElement?.stat_type && !isElementLoading">
      <button
        class="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-lg font-medium"
        @click="editElement"
      >
        <FontAwesomeIcon :icon="['fal', 'chart-bar']" size="3x" />
        <span class="">{{
          $t("le type de statistique n'est pas défini")
        }}</span>
      </button>
    </template>
    <template v-else-if="!isElementLoading">
      <div
        v-if="localElement?.format === 'compact'"
        class="absolute inset-x-0 bottom-0 flex flex-row items-center justify-center pb-3"
      >
        <button
          class="flex flex-row gap-1.5 rounded-full bg-gray-200 p-1 font-bold opacity-0 transition ease-in-out group-hover:opacity-100"
          @click="changeView"
        >
          <span
            class="h-2 w-2 rounded-full"
            :class="[view === 'chart' ? 'bg-gray-500' : 'bg-gray-800']"
          ></span>
          <span
            class="h-2 w-2 rounded-full"
            :class="[view === 'data' ? 'bg-gray-500' : 'bg-gray-800']"
          ></span>
        </button>
      </div>
      <div class="flex-shrink-0">
        <h3 class="text-xl font-bold leading-none">
          {{ $trans(localElement?.title) }}
        </h3>
        <div v-if="localElement?.custom_filters" class="text-gray-400">
          <h3>
            <FontAwesomeIcon
              :icon="['fal', 'exclamation-circle']"
              class="text-gray-400"
            />
            {{ $t('global.custom_filters_set') }}
          </h3>
        </div>
      </div>
      <div class="flex min-h-0 flex-1 flex-row gap-2">
        <div
          v-if="
            (localElement?.format === 'extended' || view === 'chart') &&
            localElement?.data?.datasets?.[0] &&
            localElement?.data?.datasets[0]?.data?.length
          "
          class="flex min-w-0 flex-1 flex-row items-center justify-center rounded-md bg-gray-50 p-1 text-2xl"
        >
          <BarChart
            v-if="localElement?.chart_type === 'bar'"
            :chart-data="localElement?.data"
            :group-by="localElement?.group_by"
          />
          <DoughnutChart
            v-else-if="localElement?.chart_type === 'doughnut'"
            :chart-data="localElement?.data"
            :group-by="localElement?.group_by"
          />
          <PieChart
            v-else-if="localElement?.chart_type === 'pie'"
            :chart-data="localElement?.data"
            :group-by="localElement?.group_by"
          />
          <LineChar
            v-else-if="localElement?.chart_type === 'line'"
            :chart-data="localElement?.data"
            :group-by="localElement?.group_by"
          />
        </div>
        <div
          v-if="
            (localElement?.format === 'extended' || view === 'data') &&
            localElement?.data?.datasets?.[0] &&
            localElement?.data?.datasets[0]?.data?.length
          "
          class="min-w-0 items-center justify-center overflow-y-auto rounded-md bg-gray-50"
          :class="fullScreen ? 'w-2/5' : 'flex-1'"
        >
          <TableChart
            v-if="Array.isArray(localElement?.data?.datasets)"
            :data="localElement?.data"
            :is-turnover="localElement?.stat_type === 'sales_turnover'"
            :compare="true"
          />
        </div>
        <div
          v-if="
            !localElement?.data ||
            (localElement?.data?.datasets?.[0] &&
              localElement?.data?.datasets[0]?.data?.length === 0) ||
            !localElement?.data?.datasets[0]
          "
          class="flex min-w-0 flex-1 flex-row items-center justify-center rounded-md bg-gray-50 p-1 text-2xl"
        >
          <span> Aucune donnée disponible pour les filtres rentrés </span>
        </div>
      </div>
    </template>
  </div>
  <ElementModal
    v-model:element="localElement"
    :open="modalOpened"
    :type="'large'"
    :filters="filters"
    @close="handleCloseModal"
    @save-dashboard="handleSaveDashboard()"
  />
</template>

<script>
import BaseDropDownMenu from '@/components/addf-package/components/dropdown/BaseDropDownMenu.vue'
import ElementModal from './ElementModal.vue'
import { mapActions, mapGetters } from 'vuex'
import BarChart from './chart/BarChart.vue'
import PieChart from './chart/PieChart.vue'
import DoughnutChart from './chart/DoughnutChart.vue'
import TableChart from './table/TableChart.vue'
import LineChar from '@/components/Dashboard/Content/Statistics/chart/LineChart.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import LoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'TileCustom',
  components: {
    LoadingComponent,
    FontAwesomeIcon,
    LineChar,
    TableChart,
    DoughnutChart,
    PieChart,
    BarChart,
    BaseDropDownMenu,
    ElementModal
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    templateDashboardId: {
      type: String,
      required: true
    },
    dashboardRowId: {
      type: String,
      required: true
    },
    filters: {
      type: Array,
      required: false,
      default: null
    }
  },
  emits: [
    'save-dashboard',
    'add-before',
    'add-after',
    'update:element',
    'delete'
  ],
  data() {
    return {
      modalOpened: false,
      view: this.element.default_view,
      fullScreen: false,
      dummy_data: {
        datasets: [
          {
            label: '2021',
            data: [300, 50, 100],
            backgroundColor: [
              'rgb(255, 99, 132)',
              'rgb(54, 162, 235)',
              'rgb(255, 205, 86)'
            ],
            borderRadius: 3,
            hoverOffset: 4
          },
          {
            data: [310, 70, 200],
            backgroundColor: [
              'rgb(255, 99, 132)',
              'rgb(54, 162, 235)',
              'rgb(255, 205, 86)'
            ],
            borderRadius: 3,
            label: '2022',
            hoverOffset: 4
          }
        ],
        labels: ['Incinération', 'Inhumation', 'Conservation']
      },
      isLoading: false,
      isElementLoading: false,
      localElement: {
        title: {},
        stat_type: null,
        period: null,
        date_to: null,
        date_from: null,
        group_by: null,
        format: 'extended'
      }
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth'
    }),
    actionMenuOptions() {
      return [
        [
          {
            label: this.$t('button.edit'),
            icon: 'edit',
            gate: true,
            action: this.editElement
          },
          {
            label:
              this.element.format === 'extended'
                ? this.$t('button.shrink')
                : this.$t('button.expand'),
            icon:
              this.element.format === 'extended'
                ? 'arrows-minimize'
                : 'arrows-maximize',
            gate: true,
            action: this.changeFormat
          },
          {
            label: this.$t('global.button.dashboard.agenda.view'),
            icon: 'table',
            gate: true,
            action: this.changeFormat
          },
          {
            label: this.$t('button.export'),
            icon: 'file-export',
            gate: true,
            action: this.excelExport
          }
        ],
        [
          {
            label: this.$t('global.button.dashboard.analytics.add_before'),
            icon: 'plus',
            gate: true,
            action: () => this.$emit('add-before')
          },
          {
            label: this.$t('global.button.dashboard.analytics.add_after'),
            icon: 'plus',
            gate: true,
            action: () => this.$emit('add-after')
          }
        ],
        [
          {
            label: this.$t('button.delete'),
            icon: 'trash',
            gate: true,
            bg_color: 'bg-red-500',
            txt_color: 'text-white',
            action: () => this.$emit('delete')
          }
        ]
      ]
    }
  },
  watch: {
    element: {
      handler(newValue, _oldValue) {
        // this.fetchTemplateElement()
        this.isSingleElementLoading = false
      },
      deep: true
    },
    filters: {
      handler(newValue, oldValue) {
        if (!this.localElement.custom_filters) {
          this.localElement.filters = []
          for (const newValueKey in newValue) {
            if (
              newValue[newValueKey]['column'] !== null &&
              newValue[newValueKey]['condition'] !== null &&
              newValue[newValueKey]['values'] !== null &&
              newValue[newValueKey]['whereType'] !== null
            ) {
              this.localElement.filters.push(newValue[newValueKey])
            } else {
              break
            }
          }

          this.handleSaveDashboard()
        }
      },
      deep: true
    },
    actionMenuOptions(newValue, _oldValue) {
      // console.log(newValue)
    }
  },
  mounted() {
    if (!this.element.stat_type) {
      this.editElement()
    }
    this.fetchTemplateElement()
  },
  methods: {
    ...mapActions({
      processFetchStatisticsForExportAction:
        'statistics/fetchStatisticsForExport',
      processFetchTemplateDashboardElementByIdAction:
        'dashboard/fetchTemplateDashboardElementById',

      processUpdateTemplateDashboardElementByIdAction:
        'dashboard/updateTemplateDashboardElementById',

      processAddTemplateDashboardElementByIdAction:
        'dashboard/addTemplateDashboardElementById'
    }),
    changeFormat() {
      this.localElement.format =
        this.localElement.format === 'extended' ? 'compact' : 'extended'
      this.handleSaveDashboard()
    },
    excelExport() {
      this.isLoading = true
      this.processFetchStatisticsForExportAction({
        id: this.element.id,
        element: this?.localElement,
        successCallback: () => {
          this.isLoading = false
          // this.$h.toastify(this.$t('toasts.import_list_with_success'), {
          //   className: 'toastify-content success'
          // })
        },
        errorCallback: () => {
          this.$h.toastify(
            this.$t('global.error_when_import_file'),
            { className: 'toastify-content warning' },
            (this.isErrorModalOpen = true)
          )
        }
      })
    },
    handleExportFile() {
      this.isLoading = true
      this.fetchStatisticsForExport({
        successCallback: () => {
          this.isLoading = false
        }
      })
    },
    editElement() {
      this.modalOpened = true
    },
    handleCloseModal() {
      this.modalOpened = false
    },
    changeView() {
      this.view = this.view === 'chart' ? 'data' : 'chart'
    },
    handleSaveDashboard() {
      if (this?.localElement?.id) {
        if (!this?.localElement.custom_filters) {
          if (
            this?.filters?.length &&
            this?.filters[(this?.filters).length - 1].values !== null
          ) {
            this.localElement.filters = this?.filters
          }
        }
        this.processUpdateTemplateDashboardElementByIdAction({
          id: this?.templateDashboardId,
          element: this?.localElement
        }).then((response) => {
          this.orderedElement(response?.data?.element)
        })
      } else if (!this?.localElement?.id) {
        this.processAddTemplateDashboardElementByIdAction({
          id: this?.templateDashboardId,
          rowId: this?.dashboardRowId,
          element: this?.localElement
        }).then((response) => {
          this.orderedElement(response?.data?.element)
          this.isElementLoading = false
        })
      }
    },

    fetchTemplateElement() {
      this.isElementLoading = true
      if (this?.element?.row?.template_dashboard_id) {
        this.processFetchTemplateDashboardElementByIdAction({
          id: this?.element?.row?.template_dashboard_id,
          element_id: this?.element?.id
        }).then((response) => {
          this.orderedElement(response?.data?.element)
          this.isElementLoading = false
        })
      }
    },
    orderedElement(element) {
      if (element.data.datasets && element.stat_type !== 'sales_turnover') {
        let combinedArray = []
        const data = element.data.datasets[0]?.data

        if (['cremation_count', 'burial_count'].includes(element.stat_type)) {
          combinedArray = element.data.labels
            .map((label) => ({
              label,
              value: data[0][label] // Default to 0 if not found
            }))
            .filter((item) => item.value > 0) // Remove entries with no value
            .sort((a, b) => b.value - a.value) // Sort by value descending
        } else {
          combinedArray = element.data.labels
            ?.map((label, index) => ({
              label,
              value: data[0][Object.keys(data[0])[index]]
            }))
            .filter((item) => item.value)
            .sort((a, b) => b.value - a.value)
        }

        element.data.datasets[0].data = combinedArray.map((item) => item.value)
        element.data.labels = combinedArray.map((item) => item.label)
      } else {
        const datasets = element.data.datasets[0]?.data || []

        let combinedArray = Object.entries(datasets[0])
          .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
          .map(([label, value]) => ({
            label,
            value
          }))
          .flat()

        // Update the element with the sorted values and labels
        element.data.datasets[0].data = combinedArray.map((item) => item.value)
        element.data.labels = combinedArray.map((item) => item.label)
        element.data = this.transformData(element.data)
      }
      this.localElement = element
    },
    transformData(originalData) {
      let uniqueLabelStrings = [
        ...new Set(originalData.labels.map((item) => item.split(' Total')[0]))
      ]

      const transformedData = {
        labels: uniqueLabelStrings,
        datasets: []
      }

      // Extract the HTVA and TVAC data
      const htvaData = []
      const tvacData = []
      const deboursData = []

      originalData.labels.forEach((label, index) => {
        if (label.includes('HTVA')) {
          htvaData.push(originalData.datasets[0].data[index])
        } else if (label.includes('TVAC')) {
          tvacData.push(originalData.datasets[0].data[index])
        } else if (label.includes('Debours')) {
          deboursData.push(originalData.datasets[0].data[index])
        }
      })

      // Create HTVA dataset
      if (htvaData.length > 0) {
        transformedData.datasets.push({
          label: 'HTVA',
          data: htvaData,
          borderColor: '#90c163',
          backgroundColor: '#51bc56',
          borderRadius: 3,
          borderWidth: 2,
          fill: 'rgba(125, 123, 255, 0.2)',
          tension: 0.1
        })
      }
      if (tvacData.length > 0) {
        // Create TVAC dataset
        transformedData.datasets.push({
          label: 'TVAC',
          data: tvacData,
          borderColor: '#4386C8',
          backgroundColor: '#699ed3',
          borderWidth: 2,
          borderRadius: 3,
          fill: false,
          tension: 0.1
        })
      }
      if (deboursData.length > 0) {
        // Assuming 'Hors débours' data is static, add it to the datasets
        transformedData.datasets.push({
          label: 'Hors débours',
          data: deboursData, // Replace with dynamic data if available
          borderColor: '#E53D50',
          backgroundColor: '#ea6473',
          borderRadius: 3,
          borderWidth: 2,
          fill: false,
          tension: 0.1
        })
      }

      return transformedData
    }
  }
}
</script>

<style scoped></style>
